import {askYesOrNo} from 'src/components/modals/ChoicesModal';
import {getDhlItemPrice} from 'src/pages/CreateDhlPage/useDhlForm';
import {getUtcDateTimeFromISO} from 'src/utils/date';
import {calcPriceAfterDiscount} from 'src/utils/discountUtils';
import {isNullOrEmpty} from 'src/utils/helperMethods';
import {getNumber} from 'src/utils/numberUtils';
import {getAddress} from 'src/utils/orderUtils';
import {getSite, getSiteCurrency} from 'src/utils/selectors';
import {accountIs} from 'src/utils/siteUtils';
import {getFormattedPhoneNumber} from 'src/utils/stringUtils';

const printJS = require('print-js');
const {printReceipt} = require('src/utils/AdminApi');

function base64ArrayBuffer(arrayBuffer) {
	var base64 = '';
	var encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

	var bytes = new Uint8Array(arrayBuffer);
	var byteLength = bytes.byteLength;
	var byteRemainder = byteLength % 3;
	var mainLength = byteLength - byteRemainder;

	var a, b, c, d;
	var chunk;

	// Main loop deals with bytes in chunks of 3
	for (var i = 0; i < mainLength; i = i + 3) {
		// Combine the three bytes into a single integer
		chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

		// Use bitmasks to extract 6-bit segments from the triplet
		a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
		b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
		c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
		d = chunk & 63; // 63       = 2^6 - 1

		// Convert the raw binary segments to the appropriate ASCII encoding
		base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
	}

	// Deal with the remaining bytes and padding
	if (byteRemainder == 1) {
		chunk = bytes[mainLength];

		a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

		// Set the 4 least significant bits to zero
		b = (chunk & 3) << 4; // 3   = 2^2 - 1

		base64 += encodings[a] + encodings[b] + '==';
	} else if (byteRemainder == 2) {
		chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

		a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
		b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

		// Set the 2 least significant bits to zero
		c = (chunk & 15) << 2; // 15    = 2^4 - 1

		base64 += encodings[a] + encodings[b] + encodings[c] + '=';
	}

	return base64;
}
/**
@Deprecated
*/
export const printOrderReceipt = async (orderId, orderUID) => {
	const pdfFile = await printReceipt(orderId, orderUID);
	printJS({printable: base64ArrayBuffer(pdfFile), type: 'pdf', base64: true});
};

const receiptStyle = ` table{
	margin-bottom:18px;
	width:100%;
	border-collapse: collapse;} th, td {
	text-align:right;
	padding:8px;
	border:0.5px dashed black;
  } html{font-size:14px;direction:rtl;font-family:default} .custom-h3 { color: red; }`;

/**
 *
 * @param {import('vetrina-types').ContentTypeAttributes<'orders'>} order
 * @param {{show_weight?: boolean, delivery_fee_enabled?: boolean, use_fixed_price?: boolean, hardcoded_price?: number, currency?: string}} options
 */
export const getReceiptHtml = (order, options = {}) => {
	const {site_name, host, logo, ...site} = getSite();
	const {
		show_weight = host === 'alassalsweets.com',
		delivery_fee_enabled = true,
		use_fixed_price = false,
		hardcoded_price = 0,
		currency = getSiteCurrency(),
	} = options;

	const cur = currency;
	const logoSize = 80;
	const order_settings = site.config.order_settings ?? {};
	const logoImgTag =
		order_settings.receipt_show_logo && logo?.url
			? `<img width='${logoSize}' height='${logoSize}' src='${logo.url}'/>`
			: '';
	const siteNameTag =
		(order_settings.receipt_show_sitename ?? true)
			? `<h1 style="text-align:center;" >${site_name}</h1>`
			: '';
	const ordersItems = order.items
		.filter((item) => !item.isDeleted)
		.map((item) => {
			const item_price = calcPriceAfterDiscount(item.price, item.discount);
			const price = use_fixed_price ? hardcoded_price : item_price;
			const total = use_fixed_price
				? hardcoded_price
				: calcPriceAfterDiscount(item.price, item.discount) * item.quantity;
			return {
				name: `${item.product?.name ?? item.name} ${item.variation?.name ?? ''}`,
				price,
				total,
				quantity: item.quantity,
				sku: item.product?.sku,
				weight: item.product?.weight,
			};
		});

	const totalQty = ordersItems.reduce((sum, item) => sum + item.quantity, 0);
	const totalWeight = ordersItems.reduce(
		(sum, item) => sum + (isNullOrEmpty(item.weight) ? 0 : item.weight * item.quantity),
		0,
	);

	const subTotal = !use_fixed_price ? order.subTotal : hardcoded_price * totalQty;
	const total = !use_fixed_price ? order.total : subTotal;

	const getRow = (title, body) => {
		if (isNullOrEmpty(body)) return '';
		return `<p><strong>${title}</strong>: ${body}</p>`;
	};

	const orderItemsTable = `
		<table>
			<tr>
				<th>المنتج</th>
				<th>السعر</th>
				<th>الكمية</th>
				<th>المجموع</th>
				${show_weight ? '<th>الوزن</th>' : ''}
			</tr>
			${ordersItems
				.map(
					(item) => `
				<tr>
					<td>
					<span>${item.name}</span>
					<br/>
					<span>${!isNullOrEmpty(item.sku) ? item.sku : ''}</span>
					</td>
					<td>${item.price}</td>
					<td>${item.quantity}</td>
					<td>${item.total}</td>
					${show_weight ? `<td>${!isNullOrEmpty(item.weight) ? `${getNumber(item.weight).toFixed(1)} كغ` : '-'}</td>` : ''}
				</tr>
				`,
				)
				.join('')}
		</table>
	`;
	const noteTag = !isNullOrEmpty(order.note)
		? `<pre><strong>ملاحظـة</strong>: ${order.note}</pre>`
		: '';

	const phoneString = !isNullOrEmpty(order.customer?.phone)
		? getRow('رقم الهاتف', getFormattedPhoneNumber(order.customer?.phone))
		: '';
	const emailString = getRow('البريد الالكتروني', order.customer?.email);

	const phone2String = !isNullOrEmpty(order.custom_fields?.phone2)
		? getRow('رقم الهاتف الاحتياطي', getFormattedPhoneNumber(order.custom_fields?.phone2))
		: '';
	const selectedDateString = !isNullOrEmpty(order.selected_date)
		? getRow('تاريخ التسليم', getUtcDateTimeFromISO(order.selected_date))
		: '';
	const creatorString =
		accountIs('alassalsweets', true) && !isNullOrEmpty(order.creator)
			? getRow('بواسطة', order.creator.name)
			: '';
	const addressString = order.full_address; //getAddress(order, true);
	const cusomterNameString = !isNullOrEmpty(order.customer?.name)
		? getRow('اسم الزبون', order.customer?.name)
		: '';
	// show subtotal alone only when there is delivery fees
	const includeDeliveryFees = getNumber(order.delivery_price) > 0 && delivery_fee_enabled;
	const subTotalString = includeDeliveryFees ? getRow('مجموع المشتريات', subTotal + cur) : '';
	const discountString = order.discount > 0 ? getRow('التخفيض', order.discount + cur) : '';
	const deliveryPriceString = includeDeliveryFees
		? getRow('سعر التوصيل', order.delivery_price + cur)
		: '';

	const shipping_tracking_id = getRow('رقم تتبع الشحنة', order.delivery_info?.shippingId);

	const down_payment_text =
		order.down_payment > 0
			? `					<p><strong>العربون:<strong>${order.down_payment}</p>
		<p ><strong>المتبقي</strong>: ${order.total - order.down_payment}</p>`
			: '';
	const payment_received_text = order.payment_received
		? `<p  style="font-size:22px"> <strong>تم الدفع</strong></p>`
		: '';

	const html = `
		<div style="display:flex;flex-direction:column;justify-content:center;align-items:center;">
		${logoImgTag}
		${siteNameTag}
		</div>
		${getRow('رقم الطلب: ', order.tracking_number)}
		${cusomterNameString}
		${emailString}
		${getRow('تاريخ انشاء الطلب', getUtcDateTimeFromISO(order.created_at))}
 	  	${addressString}
 	  	${shipping_tracking_id}
		${phoneString}
		${phone2String}
 		${orderItemsTable}
 		${show_weight ? getRow('الوزن الكلي', `${getNumber(totalWeight).toFixed(1)} كغ`) : ''}
 		${subTotalString}
 		${deliveryPriceString}
 		${discountString}
		<p><strong>المجموع الكلي</strong>: ${total}${cur}</p>
		${down_payment_text}
		${payment_received_text}

		${selectedDateString}
		${noteTag}
		${creatorString}
		<hr/>
		${order_settings.receipt_footnote ?? ''}
		<div style="margin-bottom:18px;"></div>
 	`;

	return {html, style: receiptStyle};
};

/**
 *
 * @param {import('vetrina-types').ContentTypeAttributes<'orders'>} order
 * @param {{show_weight?: boolean, delivery_fee_enabled?: boolean, use_fixed_price?: boolean, hardcoded_price?: number, currency?: string}} options
 */
export const printJsonReceipt = (order, options = {}) => {
	const {html, style} = getReceiptHtml(order, options);
	printJS({
		printable: html,
		type: 'raw-html',
		header: 'header',
		style,
	});
};

export const printProductPrice = (product) => {
	const {site_name, logo, ...site} = getSite();
	const cur = getSiteCurrency();
	const withoutDiscount = `<p style="font-size: 24px; ">${product.price}${cur}</p>`;
	const withDiscount = `
	<div style="display: flex;flex-direction:column;  align-items:center;">
		<p style="font-size: 24px; text-decoration: line-through;">${product.price}${cur}</p>
		<p style="font-size: 24px; font-weight:bold; color: red;">${calcPriceAfterDiscount(
			product.price,
			product.discount,
		)}${cur}</p>
	</div>`;

	const html = `
	<div style="display: flex; flex-direction: column; gap:4px; align-items: center;">
		<h1 style="font-size: 48px;">${site_name}</h1>
		${product.discount ? withDiscount : withoutDiscount}
	</div>
	`;
	printJS({
		printable: html,
		type: 'raw-html',
		style: '*{margin:0;padding:0; font-family:sans-serif} html {direction: rtl;}',
	});
};
